import { FC, useState } from 'react'
import {
  TextField,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Button,
  Stack,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
  Divider
} from '@mui/material'
import { usePersonsList } from '@services/contacts/contacts.hooks'
import { Person } from '@services/contacts/contacts.models'
import {
  Search as SearchIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material'
import { anonymizeEmail, anonymizePhoneNumber, canViewFullClientData } from '@utils/anonymization'

interface ClientSearchProps {
  onSelectPerson: (person: Person) => void
}

const ClientSearch: FC<ClientSearchProps> = ({ onSelectPerson }) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [showFullData, setShowFullData] = useState(false)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const { data, isLoading, error } = usePersonsList(searchTerm.trim() || undefined)
  const canViewFull = canViewFullClientData()

  const handleSearch = (value: string) => {
    setSearchTerm(value)
  }

  const displayEmail = (email: string) => {
    if (!email) return ''
    return showFullData || canViewFull ? email : anonymizeEmail(email)
  }

  const displayPhone = (phone: string) => {
    if (!phone) return ''
    return showFullData || canViewFull ? phone : anonymizePhoneNumber(phone)
  }

  const toggleDataVisibility = () => {
    setShowFullData(!showFullData)
  }

  return (
    <Box>
      <Stack spacing={2}>
        <TextField
          fullWidth
          label='Wyszukaj klienta'
          placeholder='Wprowadź imię, nazwisko, email lub numer telefonu'
          value={searchTerm}
          onChange={(e) => handleSearch(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon color='action' sx={{ mr: 1 }} />
          }}
        />

        {error && <Typography color='error'>Błąd podczas wyszukiwania klientów</Typography>}

        {isLoading && (
          <Box display='flex' justifyContent='center' p={2}>
            <CircularProgress />
          </Box>
        )}

        {!searchTerm.trim() && !isLoading && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant='body1' color='text.secondary' gutterBottom>
              Rozpocznij wpisywanie, aby wyszukać klientów
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Możesz szukać po imieniu, nazwisku, emailu lub numerze telefonu
            </Typography>
          </Box>
        )}

        {data && searchTerm.trim() && (
          <Box>
            <Stack
              direction='row'
              justifyContent='space-between'
              alignItems='center'
              sx={{ mb: 1 }}
            >
              <Typography variant='body2' color='text.secondary'>
                Znaleziono {data.count} klientów
              </Typography>

              {!canViewFull && (
                <Button
                  size='small'
                  variant='outlined'
                  startIcon={<VisibilityIcon />}
                  onClick={toggleDataVisibility}
                  sx={{ minWidth: 'auto' }}
                >
                  {showFullData ? 'Ukryj dane' : 'Pokaż dane'}
                </Button>
              )}
            </Stack>

            {isMobile ? (
              // Mobile Card Layout
              <Stack spacing={2}>
                {data.results.map((person) => (
                  <Card key={person.id} variant='outlined'>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Stack spacing={1}>
                        <Box>
                          <Typography variant='subtitle1' fontWeight='medium'>
                            {person.firstName} {person.lastName}
                          </Typography>
                          {person.company && (
                            <Typography variant='caption' color='text.secondary'>
                              {person.company}
                            </Typography>
                          )}
                        </Box>

                        <Divider />

                        <Stack spacing={1}>
                          {person.email && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <EmailIcon fontSize='small' color='action' />
                              <Typography variant='body2' sx={{ wordBreak: 'break-all' }}>
                                {displayEmail(person.email)}
                              </Typography>
                            </Box>
                          )}

                          {person.mobile && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <PhoneIcon fontSize='small' color='action' />
                              <Typography variant='body2'>{displayPhone(person.mobile)}</Typography>
                            </Box>
                          )}
                        </Stack>

                        <Box sx={{ pt: 1 }}>
                          <Button
                            fullWidth
                            variant='contained'
                            onClick={() => onSelectPerson(person)}
                            size='medium'
                          >
                            Edytuj
                          </Button>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            ) : (
              // Desktop Table Layout
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Imię i nazwisko</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Telefon</TableCell>
                      <TableCell>Akcje</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.results.map((person) => (
                      <TableRow key={person.id}>
                        <TableCell>
                          <Typography variant='body2' fontWeight='medium'>
                            {person.firstName} {person.lastName}
                          </Typography>
                          {person.company && (
                            <Typography variant='caption' color='text.secondary'>
                              {person.company}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{displayEmail(person.email)}</TableCell>
                        <TableCell>
                          {person.mobile && (
                            <Typography variant='body2'>{displayPhone(person.mobile)}</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            size='small'
                            variant='outlined'
                            onClick={() => onSelectPerson(person)}
                          >
                            Edytuj
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {data.results.length === 0 && searchTerm.trim() && (
              <Typography color='text.secondary' textAlign='center' py={4}>
                Nie znaleziono klientów
              </Typography>
            )}
          </Box>
        )}
      </Stack>
    </Box>
  )
}

export default ClientSearch
